import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { metaService } from "services/metaService";
import { useMemo } from "react";
import { 
  MetaRequest, 
  MetaResponse, 
  MetaResumida, 
  UsuarioMetaDto, 
  MetaFilters, 
  UsuarioFilters,
  RankingPerformanceDto,
  Result
} from "types/meta";

// Query keys
export const metaKeys = {
  all: ['metas'] as const,
  list: (filters: MetaFilters) => [...metaKeys.all, 'list', filters] as const,
  detail: (id: string) => [...metaKeys.all, 'detail', id] as const,
  usuarios: (filters: UsuarioFilters) => [...metaKeys.all, 'usuarios', filters] as const,
  ranking: (startDate?: string, endDate?: string) => [...metaKeys.all, 'ranking', startDate, endDate] as const,
  contagem: () => [...metaKeys.all, 'contagem'] as const,
};

// Query para buscar metas
export const useMetas = (filters: MetaFilters = {}, options: { enabled?: boolean } = {}) => {
  const { getQueryRetryHandler } = usePermissionModal();
  const queryClient = useQueryClient();

  const metasQuery = useQuery({
    queryKey: metaKeys.list(filters),
    queryFn: async () => {
      // Chama ambas as funções em paralelo
      const [metasResult, contagemResult] = await Promise.all([
        metaService.buscarMetas(filters),
        metaService.contarMetasPorStatus()
      ]);
      
      // Atualiza o cache da contagem separadamente
      queryClient.setQueryData(metaKeys.contagem(), contagemResult);
      
      return metasResult;
    },
    enabled: options.enabled ?? true,
    retry: getQueryRetryHandler({
      queryKey: metaKeys.list(filters),
      defaultMessage: "Você não possui permissão para consultar metas."
    }),
  });

  return metasQuery;
};

// Query para buscar contagem de metas por status
export const useContagemMetasPorStatus = () => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: metaKeys.contagem(),
    queryFn: () => metaService.contarMetasPorStatus(),
    staleTime: 1 * 60 * 1000, // 1 minuto
    retry: getQueryRetryHandler({
      queryKey: metaKeys.contagem(),
      defaultMessage: "Você não possui permissão para consultar contagem de metas."
    }),
  });
};

// Query para buscar meta por ID
export const useMeta = (id: string) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: metaKeys.detail(id),
    queryFn: () => metaService.buscarMetaPorId(id),
    enabled: !!id,
    retry: getQueryRetryHandler({
      queryKey: metaKeys.detail(id),
      defaultMessage: "Você não possui permissão para consultar esta meta."
    }),
  });
};

// Query para buscar usuários vendedores
export const useUsuariosVendedores = () => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: [...metaKeys.all, 'usuarios-vendedores'],
    queryFn: () => metaService.buscarUsuariosVendedores(),
    retry: getQueryRetryHandler({
      queryKey: [...metaKeys.all, 'usuarios-vendedores'],
      defaultMessage: "Você não possui permissão para consultar usuários vendedores."
    }),
  });
};

// Query para buscar usuários (método antigo - manter por compatibilidade)
export const useUsuarios = (filters: UsuarioFilters = {}) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: metaKeys.usuarios(filters),
    queryFn: () => metaService.buscarUsuarios(filters),
    retry: getQueryRetryHandler({
      queryKey: metaKeys.usuarios(filters),
      defaultMessage: "Você não possui permissão para consultar usuários."
    }),
  });
};

// Mutation para criar meta
export const useCriarMeta = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (meta: MetaRequest) => metaService.criarMeta(meta),
    onSuccess: (result) => {
      if (result.isSuccess) {
        queryClient.invalidateQueries({ queryKey: metaKeys.all });
        queryClient.invalidateQueries({ queryKey: metaKeys.contagem() });
      }
    },
  });
};

// Mutation para atualizar meta
export const useAtualizarMeta = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, meta }: { id: string; meta: MetaRequest }) => 
      metaService.atualizarMeta(id, meta),
    onSuccess: (result, variables) => {
      if (result.isSuccess) {
        queryClient.invalidateQueries({ queryKey: metaKeys.all });
        queryClient.invalidateQueries({ queryKey: metaKeys.detail(variables.id) });
        queryClient.invalidateQueries({ queryKey: metaKeys.contagem() });
      }
    },
  });
};

// Mutation para excluir meta
export const useExcluirMeta = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (id: string) => metaService.excluirMeta(id),
    onSuccess: (result, id) => {
      if (result.isSuccess) {
        queryClient.invalidateQueries({ queryKey: metaKeys.all });
        queryClient.removeQueries({ queryKey: metaKeys.detail(id) });
        queryClient.invalidateQueries({ queryKey: metaKeys.contagem() });
      }
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para excluir metas.",
      (error) => {
        // Handle non-permission errors - será tratado pelo componente
        throw error;
      }
    ),
  });
};

// Query para buscar ranking de performance com React Query
export const useRankingPerformance = (startDate?: string, endDate?: string, options: { enabled?: boolean } = {}) => {
  const { getQueryRetryHandler } = usePermissionModal();

  // Estabilizar as query keys para evitar re-renders desnecessários
  const queryKey = useMemo(() => metaKeys.ranking(startDate, endDate), [startDate, endDate]);
  const isEnabled = options.enabled ?? true;

  return useQuery({
    queryKey,
    queryFn: () => metaService.buscarRankingPerformance(startDate, endDate),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutos - dados não mudam com frequência
    gcTime: 10 * 60 * 1000, // 10 minutos no cache
    refetchOnWindowFocus: false, // Evita refetch desnecessário
    refetchOnMount: false, // Usa cache se disponível
    refetchInterval: false, // Sem polling automático
    retry: getQueryRetryHandler({
      queryKey,
      defaultMessage: "Você não possui permissão para consultar o ranking de performance."
    }),
  });
};
